import {getTutorialCatalogDrift} from './tutorialModules';

const moduleTours = [
    {
        key: 'dashboard-operativo',
        sidebar: 'Tablero',
        match: pathname => pathname.startsWith('/dashboard'),
        introTitle: 'Tablero operativo',
        introDescription: 'Este submódulo funciona como punto de entrada del sistema. Aquí verás indicadores, widgets y accesos rápidos para comenzar tu jornada.',
        pageTitle: 'Lectura de dashboard',
        pageDescription: 'Cada tablero concentra distinta información resumida. La idea es detectar pendientes, volumen operativo y accesos a tareas frecuentes sin navegar de inmediato a otras pantallas.',
        steps: [
            {
                selector: '[data-tour-dashboard-kpis]',
                title: 'Indicadores clave del día',
                description: 'Estos bloques resumen pacientes, estudios, alertas de inventario, citas de hoy, pipeline comercial y colaboradores activos.'
            },
            {
                selector: '[data-tour-dashboard-business-overview]',
                title: 'Vista ejecutiva de negocio',
                description: 'Aquí puedes revisar ingresos, egresos (incluidas compras de inventario), flujo neto, conversión comercial y foco operativo semanal.'
            },
            {
                selector: '[data-tour-dashboard-patients-trend]',
                title: 'Tendencia de crecimiento',
                description: 'Esta gráfica muestra la evolución de pacientes en los últimos meses para detectar cambios de demanda.'
            },
            {
                selector: '[data-tour-dashboard-patients-company]',
                title: 'Distribución por empresa',
                description: 'Visualiza que empresas concentran mayor volumen de pacientes para priorizar seguimiento comercial y operativo.'
            },
            {
                selector: '[data-tour-dashboard-inventory-alerts]',
                title: 'Alertas de abastecimiento',
                description: 'Este widget marca productos por debajo del límite de recompra para anticipar compras y evitar faltantes.'
            },
            {
                selector: '[data-tour-dashboard-real-data]',
                title: 'Detalle operativo reciente',
                description: 'Aquí puedes validar altas de pacientes e incidencias de stock bajo con información puntual para acción inmediata.'
            }
        ]
    },
    {
        key: 'agenda-calendario',
        sidebar: 'Agenda',
        match: pathname => pathname === '/agenda' || pathname === '/doctor_appointments',
        introTitle: 'Agenda general',
        introDescription: 'Este submódulo muestra la agenda activa del equipo médico. Desde aquí puedes revisar eventos programados, abrir su detalle y detectar huecos disponibles.',
        pageTitle: 'Vista calendario',
        pageDescription: 'La pantalla principal se organiza alrededor del calendario. Según la vista elegida podrás revisar el día, la semana o una lectura más amplia del mes.',
        steps: [
            {
                selector: '[data-tour-agenda-register]',
                title: 'Crear un nuevo evento',
                description: 'Este botón abre el formulario de alta para registrar citas, compromisos internos u otros eventos operativos en la agenda.'
            },
            {
                selector: '[data-tour-agenda-calendar]',
                title: 'Calendario interactivo',
                description: 'Aquí se concentran los eventos cargados. Al hacer clic sobre una cita puedes revisar su información, editarla o eliminarla si tienes permisos.'
            }
        ]
    },
    {
        key: 'agenda-registro',
        sidebar: 'Agenda',
        match: pathname => pathname === '/agenda/register',
        introTitle: 'Formulario de agenda',
        introDescription: 'Este submódulo registra compromisos y citas para uno o varios doctores con horarios validados.',
        pageTitle: 'Captura de compromiso',
        pageDescription: 'Completa fecha, rango horario, título, tipo de compromiso y doctores asignados antes de guardar.',
        steps: [
            {
                selector: '[data-tour-agenda-form]',
                title: 'Campos principales del evento',
                description: 'Aquí defines fecha, hora de inicio y fin, título y descripción del compromiso.'
            },
            {
                selector: '[data-tour-agenda-type]',
                title: 'Origen del tipo de compromiso',
                description: 'Este catálogo viene del módulo Agenda > Tipos de compromiso. Si falta una opción, primero regístrala en ese submódulo.'
            },
            {
                selector: '[data-tour-agenda-type]',
                title: 'Ir al catálogo relacionado',
                description: 'Desde este acceso lateral puedes abrir el módulo fuente para crear o actualizar tipos de compromiso y luego volver al formulario.'
            },
            {
                selector: '[data-tour-agenda-doctors]',
                title: 'Doctores asignados',
                description: 'Los doctores disponibles se toman del módulo Configuración > Perfiles, filtrados por rol Médico.'
            },
            {
                selector: '[data-tour-agenda-actions]',
                title: 'Guardar o cancelar',
                description: 'Estas acciones confirman el alta en agenda o cancelan el registro.'
            }
        ]
    },
    {
        key: 'historia-clinica-listado',
        sidebar: 'Pacientes',
        sidebarLink: '/historia-clinica',
        match: pathname => pathname === '/historia-clinica',
        introTitle: 'Historia clínica',
        introDescription: 'Este submódulo concentra las historias clínicas por paciente. Desde aquí seleccionas un paciente, consultas folios y accedes a registro, edición o PDF.',
        pageTitle: 'Listado por paciente',
        pageDescription: 'Primero debes elegir un paciente para ver sus historias. También puedes abrir el alta de una nueva historia clínica.',
        steps: [
            {
                selector: '[data-tour-clinical-patient-picker]',
                title: 'Selección de paciente',
                description: 'Usa este control para buscar y elegir el paciente cuyas historias clínicas quieres consultar.'
            },
            {
                selector: '[data-tour-clinical-register]',
                title: 'Nueva historia clínica',
                description: 'Este acceso abre el formulario multipaso para registrar una historia nueva o continuar con un paciente ya seleccionado.'
            },
            {
                selector: '[data-tour-clinical-table]',
                title: 'Historias del paciente',
                description: 'Cuando hay un paciente activo, esta tabla muestra folio, estado, médico, dictamen y acciones de ver, PDF, editar o eliminar.'
            }
        ]
    },
    {
        key: 'historia-clinica-registro',
        sidebar: 'Pacientes',
        sidebarLink: '/historia-clinica',
        match: pathname => pathname === '/historia-clinica/registrar',
        introTitle: 'Registro de historia clínica',
        introDescription: 'Este submódulo captura la historia clínica ocupacional en secciones. Puedes iniciar con paciente existente o dar de alta uno nuevo.',
        pageTitle: 'Formulario multipaso',
        pageDescription: 'La captura se organiza por secciones médicas. Navega entre ellas, guarda borradores y finaliza cuando el expediente esté completo.',
        steps: [
            {
                selector: '[data-tour-clinical-patient-setup]',
                title: 'Configuración inicial del paciente',
                description: 'Antes de crear la historia puedes elegir paciente existente o capturar uno nuevo con datos base y empresa.'
            },
            {
                selector: '[data-tour-clinical-sections]',
                title: 'Secciones del expediente',
                description: 'Estas pestañas agrupan identificación, antecedentes, exploración, exámenes y conclusión. Úsalas para avanzar de forma ordenada.'
            },
            {
                selector: '[data-tour-clinical-sections]',
                title: 'Origen de empresas y pacientes',
                description: 'Los catálogos de empresa y paciente provienen de CRM > Empresas y Pacientes. Si falta un registro, créalo en esos módulos primero.'
            },
            {
                selector: '[data-tour-clinical-actions]',
                title: 'Guardar, PDF y finalizar',
                description: 'Desde aquí guardas el borrador, generas vista previa PDF, finalizas la historia o regresas al listado.'
            }
        ]
    },
    {
        key: 'patients-listado',
        sidebar: 'Pacientes',
        match: pathname => pathname === '/patients',
        introTitle: 'Listado de pacientes',
        introDescription: 'Este submódulo centraliza la consulta de pacientes registrados y el acceso al detalle administrativo o clínico.',
        pageTitle: 'Consulta y navegación',
        pageDescription: 'Desde esta pantalla puedes alternar modos de visualización, recorrer páginas cuando hay muchos registros, descargar expedientes y abrir el formulario de alta o edición.',
        steps: [
            {
                selector: '[data-tour-patients-pagination]',
                title: 'Paginación',
                description: 'Cuando hay más pacientes de los que caben en una página, usa Anterior y Siguiente para ver el resto del listado.'
            },
            {
                selector: '[data-tour-patients-register]',
                title: 'Alta de paciente',
                description: 'Este acceso te lleva al formulario de registro. Desde ahí podrás capturar información base del nuevo paciente.'
            }
        ]
    },
    {
        key: 'patients-registro',
        sidebar: 'Pacientes',
        match: pathname => pathname === '/register-patient',
        introTitle: 'Formulario de paciente',
        introDescription: 'Este submódulo captura datos administrativos del paciente y permite adjuntar estudios y documentos PDF.',
        pageTitle: 'Alta del paciente',
        pageDescription: 'Completa información personal, empresa, estudios opciónales y valida antes de guardar.',
        steps: [
            {
                selector: '[data-tour-patient-form]',
                title: 'Datos base del paciente',
                description: 'En esta sección capturas nombre, apellidos, sexo, fecha de nacimiento, teléfono (prefijo y 10 dígitos), domicilio y puesto.'
            },
            {
                selector: '[data-tour-patient-company]',
                title: 'Origen del campo empresa',
                description: 'La lista de empresas viene del módulo CRM > Empresas. Si no encuentras la empresa, debes crearla en ese catálogo.'
            },
            {
                selector: '[data-tour-patient-company]',
                title: 'Ir al catálogo de empresas',
                description: 'Este enlace lateral abre el módulo fuente para registrar o ajustar empresas que luego podrás seleccionar aquí.'
            },
            {
                selector: '[data-tour-patient-studies]',
                title: 'Estudios y PDFs adjuntos',
                description: 'Los estudios disponibles provienen del módulo Pacientes > Estudios y cada estudio seleccionado requiere fecha y archivos PDF.'
            },
            {
                selector: '[data-tour-patient-studies]',
                title: 'Ir al módulo de estudios',
                description: 'Si falta un estudio en la lista, abre este módulo para crearlo y vuelve a registrar el paciente.'
            },
            {
                selector: '[data-tour-patient-actions]',
                title: 'Guardar paciente',
                description: 'Aquí confirmas el registro del paciente y sus adjuntos o cancelas el formulario.'
            }
        ]
    },
    {
        key: 'studies-registro',
        sidebar: 'Pacientes',
        match: pathname => pathname === '/register-study',
        introTitle: 'Registro de estudio',
        introDescription: 'Este submódulo permite definir estudios y vincular insumos de inventario para calcular su costo operativo y su precio final.',
        pageTitle: 'Formulario del estudio',
        pageDescription: 'Aquí capturas nombre, costos adicionales, incremento y, si aplica, los objetos de inventario utilizados. Todo impacta el costo final calculado.',
        steps: [
            {
                selector: '[data-tour-study-fields]',
                title: 'Datos economicos del estudio',
                description: 'En esta seccion defines el nombre del estudio y los componentes economicos que se sumaran al costo total calculado.'
            },
            {
                selector: '[data-tour-study-inventory]',
                title: 'Inventario opcional',
                description: 'Aquí puedes agregar objetos o insumos del inventario. Cada cantidad usada impacta el costo del estudio; no es obligatorio seleccionar ninguno.'
            },
            {
                selector: '[data-tour-study-inventory]',
                title: 'Origen de objetos de inventario',
                description: 'Los objetos seleccionables vienen del módulo Inventario. Si no aparece un insumo, primero regístralo o actualízalo ahí.'
            },
            {
                selector: '[data-tour-study-summary]',
                title: 'Resumen y costo final',
                description: 'Esta tabla te ayuda a validar disponibilidad, costo unitario y cantidades seleccionadas antes de guardar el estudio.'
            }
        ]
    },
    {
        key: 'inventory-listado',
        sidebar: 'Inventario',
        match: pathname => pathname === '/inventory',
        introTitle: 'Listado de inventario',
        introDescription: 'Este submódulo te permite controlar existencias, consultar stock actual y abrir el flujo de alta o edición de productos.',
        pageTitle: 'Consulta de existencias',
        pageDescription: 'La tabla principal resume los productos de inventario y su cantidad disponible, con acciones para revisar, editar o eliminar registros.',
        steps: [
            {
                selector: '[data-tour-inventory-table]',
                title: 'Tabla de inventario',
                description: 'Aquí ves el stock por producto con acciones para revisar, editar o eliminar cada registro.'
            },
            {
                selector: '[data-tour-inventory-register]',
                title: 'Registrar producto',
                description: 'Este botón abre el formulario para dar de alta un nuevo producto dentro del inventario.'
            },
            {
                selector: '[data-tour-inventory-pagination]',
                title: 'Paginación',
                description: 'Si el catálogo crece, navega entre páginas para ver todos los productos.'
            }
        ]
    },
    {
        key: 'inventory-suppliers-list',
        sidebar: 'Inventario',
        sidebarLink: '/inventory/suppliers',
        pageSelector: '[data-widget="SettingsSuppliers"]',
        match: pathname => pathname === '/inventory/suppliers',
        introTitle: 'Menu Proveedores',
        introDescription: 'Desde Inventario entras al submodulo Proveedores para gestionar quienes surten insumos y equipos.',
        pageTitle: 'Pantalla de proveedores',
        pageDescription: 'En este modulo buscas, registras y mantienes activos los proveedores vinculados a compras y productos.',
        steps: [
            {
                selector: '[data-tour-suppliers-search]',
                title: 'Busqueda',
                description: 'Filtra por nombre, RFC, contacto o correo.'
            },
            {
                selector: '[data-tour-suppliers-register]',
                title: 'Registrar proveedor',
                description: 'Abre el formulario de alta.'
            },
            {
                selector: '[data-tour-suppliers-table]',
                title: 'Listado',
                description: 'Consulta proveedores y sus acciones.'
            }
        ]
    },
    {
        key: 'inventory-supplier-register',
        sidebar: 'Inventario',
        sidebarLink: '/inventory/suppliers',
        pageSelector: '[data-widget="RegisterSupplier"]',
        match: pathname => pathname === '/inventory/suppliers/register',
        introTitle: 'Volver al listado',
        introDescription: 'El alta de proveedor vive dentro del modulo Inventario; puedes regresar al listado desde Cancelar.',
        pageTitle: 'Formulario de proveedor',
        pageDescription: 'Captura datos del proveedor, el catalogo de productos que surte y si queda activo para compras.',
        steps: [
            {
                selector: '[data-tour-supplier-form]',
                title: 'Datos del proveedor',
                description: 'Completa nombre, RFC, contacto y notas.'
            },
            {
                selector: '[data-tour-supplier-catalog]',
                title: 'Catalogo de productos',
                description: 'Vincula los productos de inventario que este proveedor puede suministrar.'
            },
            {
                selector: '[data-tour-supplier-actions]',
                title: 'Guardar',
                description: 'Guarda o cancela para volver al listado.'
            }
        ]
    },
    {
        key: 'inventory-product-register',
        sidebar: 'Inventario',
        match: pathname => pathname === '/register-inventory-product',
        introTitle: 'Formulario de producto de inventario',
        introDescription: 'Este submódulo registra productos, equipos o insumos y aplica reglas dinámicas según su tipo de inventario.',
        pageTitle: 'Alta de producto',
        pageDescription: 'Completa nombre, tipo, cantidad y datos adicionales. Algunos campos cambian según el tipo seleccionado.',
        steps: [
            {
                selector: '[data-tour-inventory-product-form]',
                title: 'Datos del producto',
                description: 'Aquí capturas nombre, modelo, cantidad y parametros operativos como precio, calibracion o caducidad según el caso.'
            },
            {
                selector: '[data-tour-inventory-product-type]',
                title: 'Origen del tipo de inventario',
                description: 'El campo Tipo se alimenta desde Inventario > Tipos de inventario. Esa configuración define que campos se muestran en este formulario.'
            },
            {
                selector: '[data-tour-inventory-product-type]',
                title: 'Ir al módulo de tipos',
                description: 'Si necesitas otro tipo o ajustar su configuración de campos, usa este acceso lateral y luego regresa al registro de producto.'
            },
            {
                selector: '[data-tour-inventory-product-actions]',
                title: 'Guardar o cancelar',
                description: 'Estas acciones confirman el alta del producto o cancelan la captura.'
            }
        ]
    },
    {
        key: 'movements-incomes',
        sidebar: 'Movimientos',
        match: pathname => pathname === '/movements/incomes',
        introTitle: 'Submódulo de ingresos',
        introDescription: 'Aquí se concentran los ingresos registrados dentro del módulo de movimientos. Sirve para consultar altas económicas y mantener trazabilidad operativa.',
        pageTitle: 'Listado de ingresos',
        pageDescription: 'La tabla resume tipo de movimiento, cantidad y acciones disponibles para cada registro.',
        steps: [
            {
                selector: '[data-tour-movements-register]',
                title: 'Registrar ingreso nuevo',
                description: 'Usa este acceso para capturar un nuevo ingreso y clasificarlo con el tipo de movimiento correcto.'
            },
            {
                selector: '[data-tour-movements-table]',
                title: 'Tabla de ingresos',
                description: 'Aquí revisas todos los ingresos cargados y las acciones disponibles para cada uno.'
            }
        ]
    },
    {
        key: 'receivables-listado',
        sidebar: 'Movimientos',
        sidebarLink: '/movements/receivables',
        match: pathname => pathname === '/movements/receivables',
        introTitle: 'Cuentas por cobrar',
        introDescription: 'Este submódulo controla cargos pendientes de cobro a clientes. Aquí filtras por estado, revisas saldos y registras abonos.',
        pageTitle: 'Listado de cargos',
        pageDescription: 'La vista resume cliente, concepto, montos, vencimiento y estado de cada cuenta por cobrar.',
        steps: [
            {
                selector: '[data-tour-receivables-filters]',
                title: 'Filtros operativos',
                description: 'Filtra por estado del cargo o por cliente para enfocar seguimiento de cobranza.'
            },
            {
                selector: '[data-tour-receivables-register]',
                title: 'Nuevo cargo',
                description: 'Este botón abre el formulario para registrar un cargo por cobrar individual o recurrente.'
            },
            {
                selector: '[data-tour-receivables-table]',
                title: 'Tabla de cuentas',
                description: 'Aquí revisas saldo pendiente, vencimiento y acciones para ver detalle o abonar desde el listado.'
            }
        ]
    },
    {
        key: 'receivables-registro',
        sidebar: 'Movimientos',
        sidebarLink: '/movements/receivables',
        match: pathname => pathname === '/movements/receivables/register',
        introTitle: 'Registro de cargo por cobrar',
        introDescription: 'Este submódulo crea un nuevo cargo (CxC) asociado a un cliente del catálogo de empresas.',
        pageTitle: 'Captura del cargo',
        pageDescription: 'Define cliente, concepto, monto, fechas y si el cargo es recurrente antes de guardar.',
        steps: [
            {
                selector: '[data-tour-receivable-form]',
                title: 'Datos del cargo',
                description: 'Captura cliente, concepto, monto total y fechas de emisión y vencimiento.'
            },
            {
                selector: '[data-tour-receivable-form]',
                title: 'Origen del cliente',
                description: 'El cliente se toma del módulo CRM > Empresas. Si no aparece, regístralo primero en ese catálogo.'
            },
            {
                selector: '[data-tour-receivable-recurrent]',
                title: 'Cargo recurrente',
                description: 'Activa esta opción si el cobro se repetirá y selecciona la frecuencia correspondiente.'
            },
            {
                selector: '[data-tour-receivable-actions]',
                title: 'Guardar o cancelar',
                description: 'Confirma el alta del cargo o regresa al listado sin cambios.'
            }
        ]
    },
    {
        key: 'receivables-detalle',
        sidebar: 'Movimientos',
        sidebarLink: '/movements/receivables',
        match: pathname => /^\/movements\/receivables\/[^/]+$/.test(pathname),
        introTitle: 'Detalle de cuenta por cobrar',
        introDescription: 'Esta pantalla resume un cargo específico: montos cobrados, saldo pendiente, estado y abonos registrados.',
        pageTitle: 'Consulta y cobranza',
        pageDescription: 'Valida el resumen financiero del cargo y revisa el historial de abonos aplicados.',
        steps: [
            {
                selector: '[data-tour-receivable-summary]',
                title: 'Resumen financiero',
                description: 'Estas tarjetas muestran monto total, cobrado, saldo pendiente y estado actual del cargo.'
            },
            {
                selector: '[data-tour-receivable-actions]',
                title: 'Abonar o volver',
                description: 'Si hay saldo pendiente puedes registrar un abono desde aquí o regresar al listado.'
            },
            {
                selector: '[data-tour-receivable-payments]',
                title: 'Historial de abonos',
                description: 'Esta tabla lista cada abono con fecha, monto, forma de pago y notas asociadas.'
            }
        ]
    },
    {
        key: 'movements-expenses',
        sidebar: 'Movimientos',
        match: pathname => pathname === '/movements/expenses',
        introTitle: 'Submódulo de egresos',
        introDescription: 'Esta vista agrupa los egresos registrados. Te ayuda a seguir salidas de recursos y su costo total asociado.',
        pageTitle: 'Listado de egresos',
        pageDescription: 'Cada fila muestra el nombre del egreso, cantidad, costo y las acciones disponibles para operarlo.',
        steps: [
            {
                selector: '[data-tour-expenses-register]',
                title: 'Registrar egreso nuevo',
                description: 'Este botón abre el formulario de captura para crear un nuevo egreso dentro del sistema.'
            },
            {
                selector: '[data-tour-expenses-table]',
                title: 'Control de egresos',
                description: 'En esta tabla validas costos, cantidades y acciones sobre egresos ya registrados.'
            }
        ]
    },
    {
        key: 'movements-types',
        sidebar: 'Movimientos',
        match: pathname => pathname === '/movements/types',
        introTitle: 'Catalogo de tipos de movimiento',
        introDescription: 'Este submódulo administra el catálogo que clasifica ingresos y egresos. Mantenerlo limpio es clave para reporteo y consistencia operativa.',
        pageTitle: 'Gestion del catálogo',
        pageDescription: 'Aquí puedes buscar tipos, revisar su naturaleza y abrir acciones para ver, editar o eliminar registros.',
        steps: [
            {
                selector: '[data-tour-movement-types-search]',
                title: 'Búsqueda del catálogo',
                description: 'Este filtro reduce la lista por nombre o naturaleza para localizar tipos rápidamente.'
            },
            {
                selector: '[data-tour-movement-types-register]',
                title: 'Alta de tipo',
                description: 'Desde este acceso creas nuevos tipos para ingresos o egresos.'
            },
            {
                selector: '[data-tour-movement-types-table]',
                title: 'Tabla de tipos',
                description: 'Esta tabla es el catálogo operativo donde puedes revisar naturaleza, nombre y acciones de cada tipo.'
            }
        ]
    },
    {
        key: 'inventory-purchases',
        sidebar: 'Inventario',
        match: pathname => pathname === '/inventory/purchases',
        introTitle: 'Compras de inventario',
        introDescription: 'Este submódulo registra compras que incrementan existencias del inventario. Cada compra genera un egreso vinculado en Movimientos.',
        pageTitle: 'Historico de compras',
        pageDescription: 'La vista muestra compras ya registradas, cantidad total comprada y gasto total de cada operacion.',
        steps: [
            {
                selector: '[data-tour-purchases-register]',
                title: 'Registrar compra',
                description: 'Este acceso abre el formulario para capturar una compra compuesta por uno o varios productos.'
            },
            {
                selector: '[data-tour-purchases-table]',
                title: 'Listado de compras',
                description: 'Aquí puedes revisar cada compra agrupada, su fecha, volumen de productos y el gasto total registrado.'
            }
        ]
    },
    {
        key: 'movements-income-view',
        sidebar: 'Movimientos',
        match: pathname => /^\/movements\/incomes\/[^/]+$/.test(pathname),
        introTitle: 'Detalle de ingreso',
        introDescription: 'Esta pantalla muestra la información completa de un ingreso registrado, incluyendo cliente, montos y facturación.',
        pageTitle: 'Consulta del movimiento',
        pageDescription: 'Revisa los datos capturados del ingreso y accede a edición o regreso al listado.',
        steps: [
            {
                selector: '[data-tour-movement-detail]',
                title: 'Datos del ingreso',
                description: 'Aquí ves tipo de movimiento, fecha, cliente, cantidad, precio unitario, total y datos de facturación.'
            },
            {
                selector: '[data-tour-movement-detail-actions]',
                title: 'Editar o volver',
                description: 'Desde estas acciones puedes abrir la edición del ingreso o regresar al listado de ingresos.'
            }
        ]
    },
    {
        key: 'movements-expense-view',
        sidebar: 'Movimientos',
        match: pathname => /^\/movements\/expenses\/[^/]+$/.test(pathname),
        introTitle: 'Detalle de egreso',
        introDescription: 'Esta pantalla resume un egreso registrado, con proveedor, costos y vínculo a compra de inventario si aplica.',
        pageTitle: 'Consulta del movimiento',
        pageDescription: 'Valida montos y clasificación del egreso antes de editarlo o volver al listado.',
        steps: [
            {
                selector: '[data-tour-movement-detail]',
                title: 'Datos del egreso',
                description: 'Aquí revisas tipo, fecha, proveedor, cantidad, costo unitario, total y estado de facturación.'
            },
            {
                selector: '[data-tour-movement-detail-actions]',
                title: 'Editar o volver',
                description: 'Estas acciones te permiten modificar el egreso o regresar al listado de egresos.'
            }
        ]
    },
    {
        key: 'movements-income-register',
        sidebar: 'Movimientos',
        match: pathname => pathname === '/movements/incomes/register'
            || /^\/movements\/incomes\/[^/]+\/edit$/.test(pathname),
        introTitle: 'Formulario de ingreso',
        introDescription: 'Este submódulo captura un ingreso individual. La clasificación correcta del tipo de movimiento es importante para ordenar reportes y filtros.',
        pageTitle: 'Captura del ingreso',
        pageDescription: 'Debes completar nombre, tipo, cantidad y valor antes de guardar el movimiento.',
        steps: [
            {
                selector: '[data-tour-income-form]',
                title: 'Campos obligatorios',
                description: 'Aquí capturas la información base del ingreso: nombre, tipo de movimiento, cantidad y valor monetario.'
            },
            {
                selector: '[data-tour-income-form]',
                title: 'Origen del tipo de movimiento',
                description: 'El catálogo de tipos se administra en Movimientos > Tipos de movimiento. Si falta una clasificación, primero créala ahí.'
            },
            {
                selector: '[data-tour-income-actions]',
                title: 'Guardar o cancelar',
                description: 'Estas acciones cierran el flujo: puedes guardar el nuevo ingreso o volver al listado sin cambios.'
            }
        ]
    },
    {
        key: 'movements-expense-register',
        sidebar: 'Movimientos',
        match: pathname => pathname === '/movements/expenses/register'
            || /^\/movements\/expenses\/[^/]+\/edit$/.test(pathname),
        introTitle: 'Formulario de egreso',
        introDescription: 'Este submódulo sirve para registrar una salida de recursos o gasto operativo con su clasificación correspondiente.',
        pageTitle: 'Captura del egreso',
        pageDescription: 'Completa el nombre, tipo, cantidad y costo total antes de guardar el movimiento.',
        steps: [
            {
                selector: '[data-tour-expense-form]',
                title: 'Captura del egreso',
                description: 'Estos campos concentran la información principal del egreso para su registro y clasificación.'
            },
            {
                selector: '[data-tour-expense-form]',
                title: 'Origen del catálogo de tipos',
                description: 'El tipo de movimiento se toma del módulo de Tipos de movimiento. Si no existe la categoría correcta, regístrala antes de guardar.'
            },
            {
                selector: '[data-tour-expense-actions]',
                title: 'Cierre del formulario',
                description: 'Desde aquí puedes guardar el egreso o cancelar la captura y volver al listado.'
            }
        ]
    },
    {
        key: 'movements-type-register',
        sidebar: 'Movimientos',
        match: pathname => pathname === '/movements/types/register',
        introTitle: 'Formulario de tipo de movimiento',
        introDescription: 'Este submódulo administra el detalle de un tipo de movimiento. Puede servir para crear, consultar o editar registros del catálogo.',
        pageTitle: 'Definición del tipo',
        pageDescription: 'Aquí defines nombre, naturaleza, descripción y estado activo del tipo de movimiento.',
        steps: [
            {
                selector: '[data-tour-movement-type-form]',
                title: 'Definición del catálogo',
                description: 'Estos campos controlan cómo se clasifica el tipo dentro del módulo de movimientos.'
            },
            {
                selector: '[data-tour-movement-type-form]',
                title: 'Dónde se usa este catálogo',
                description: 'Los tipos registrados aquí se consumen en formularios de Ingresos y Egresos para clasificar cada movimiento.'
            },
            {
                selector: '[data-tour-movement-type-actions]',
                title: 'Acciones del registro',
                description: 'Dependiendo del modo puedes editar, guardar cambios, crear un nuevo tipo o regresar al listado.'
            }
        ]
    },
    {
        key: 'inventory-purchase-register',
        sidebar: 'Inventario',
        match: pathname => pathname === '/inventory/purchases/register',
        introTitle: 'Registro de compra de inventario',
        introDescription: 'Este submódulo captura compras de inventario compuestas. Al guardar, el stock sube y el backend genera un egreso vinculado automáticamente.',
        pageTitle: 'Compra agrupada',
        pageDescription: 'La compra se registra con detalle por producto, gasto total calculado y datos contables opcionales para el egreso auto-generado.',
        steps: [
            {
                selector: '[data-tour-purchase-supplier]',
                title: 'Proveedor de la compra',
                description: 'Selecciona el proveedor que facturo o entrego esta compra.'
            },
            {
                selector: '[data-tour-purchase-selection]',
                title: 'Seleccion de productos',
                description: 'Desde esta seccion eliges los productos incluidos en la compra y defines sus cantidades por separado.'
            },
            {
                selector: '[data-tour-purchase-selection]',
                title: 'Origen de productos comprables',
                description: 'Los productos disponibles para compra provienen del módulo Inventario. Si no aparece alguno, regístralo primero en ese módulo.'
            },
            {
                selector: '[data-tour-purchase-total]',
                title: 'Gasto total',
                description: 'Se calcula con la cantidad y el precio unitario del catálogo del proveedor. Ese monto se refleja en el egreso auto-generado.'
            },
            {
                selector: '[data-tour-purchase-payment]',
                title: 'Datos contables',
                description: 'Opcionalmente captura forma de pago y facturación. Si no los envías, el backend usa sus valores por defecto.'
            },
            {
                selector: '[data-tour-purchase-actions]',
                title: 'Guardar compra',
                description: 'Estas acciones permiten confirmar la compra o cancelar el flujo y volver al listado.'
            }
        ]
    },
    {
        key: 'crm-board',
        sidebar: 'CRM',
        match: pathname => pathname === '/crm',
        introTitle: 'Kanban de prospectos',
        introDescription: 'Este submódulo concentra el pipeline comercial. Cada columna representa una etapa del prospecto dentro del proceso comercial.',
        pageTitle: 'Tablero comercial',
        pageDescription: 'Puedes filtrar prospectos, crear nuevos registros y reordenar tarjetas con drag and drop para reflejar el avance comercial.',
        steps: [
            {
                selector: '[data-tour-crm-filters]',
                title: 'Filtros y acciones',
                description: 'Usa estos controles para buscar, filtrar por empresa, crear prospectos o recargar el tablero.'
            },
            {
                selector: '[data-tour-crm-board]',
                title: 'Pipeline visual',
                description: 'Las columnas y tarjetas representan el avance comercial. Si no hay filtros activos puedes arrastrar prospectos entre etapas.'
            }
        ]
    },
    {
        key: 'crm-companies',
        sidebar: 'CRM',
        match: pathname => pathname === '/crm/companies',
        introTitle: 'Catalogo de empresas',
        introDescription: 'Este submódulo administra las empresas relacionadas con prospectos y pacientes. Aquí puedes filtrar, registrar y mantener actualizado el catálogo.',
        pageTitle: 'Listado de empresas',
        pageDescription: 'La pantalla principal muestra el catálogo de empresas, con acciones para ver detalle, editar o eliminar cuando no hay referencias asociadas.',
        steps: [
            {
                selector: '[data-tour-companies-search]',
                title: 'Búsqueda de empresas',
                description: 'Usa este campo para filtrar rápidamente el catálogo por nombre de empresa.'
            },
            {
                selector: '[data-tour-companies-register]',
                title: 'Registrar empresa',
                description: 'Este acceso abre el formulario para crear una nueva empresa dentro del módulo CRM.'
            },
            {
                selector: '[data-tour-companies-table]',
                title: 'Tabla de empresas',
                description: 'Aquí revisas el catálogo actual y ejecutas acciones de detalle, edición o eliminación.'
            }
        ]
    },
    {
        key: 'crm-company-register',
        sidebar: 'CRM',
        match: pathname => pathname === '/crm/companies/register',
        introTitle: 'Formulario de empresa',
        introDescription: 'Este submódulo te permite crear o editar una empresa para mantener consistente la información comercial y administrativa.',
        pageTitle: 'Captura de empresa',
        pageDescription: 'Aquí defines el nombre de la empresa y cierras el flujo guardando cambios o regresando al listado.',
        steps: [
            {
                selector: '[data-tour-company-form]',
                title: 'Dato principal de empresa',
                description: 'En este campo capturas el nombre oficial de la empresa para guardarlo en el catálogo.'
            },
            {
                selector: '[data-tour-company-form]',
                title: 'Dónde se usa este catálogo',
                description: 'Las empresas registradas aquí se usan en formularios de pacientes y prospectos para asociar registros a una organización.'
            },
            {
                selector: '[data-tour-company-actions]',
                title: 'Guardar o cancelar',
                description: 'Estas acciones permiten confirmar el registro o volver al listado sin aplicar cambios.'
            }
        ]
    },
    {
        key: 'studies-listado',
        sidebar: 'Pacientes',
        match: pathname => pathname === '/studies',
        introTitle: 'Listado de estudios',
        introDescription: 'Este submódulo concentra los estudios registrados y sirve como base para revisar costos, disponibilidad y flujo de alta de nuevos estudios.',
        pageTitle: 'Consulta de estudios',
        pageDescription: 'Desde esta vista puedes revisar el listado completo de estudios y navegar al detalle o registro según tu operacion.',
        steps: [
            {
                selector: '[data-tour-studies-list]',
                title: 'Panel principal de estudios',
                description: 'Aquí se muestra la lista operativa de estudios con sus acciones disponibles.'
            },
            {
                selector: '[data-tour-studies-pagination]',
                title: 'Paginación',
                description: 'Usa Anterior y Siguiente cuando el listado se divide en varias páginas.'
            }
        ]
    },
    {
        key: 'inventory-types-listado',
        sidebar: 'Inventario',
        match: pathname => pathname === '/settings/inventory-types',
        introTitle: 'Catalogo de tipos de inventario',
        introDescription: 'Este submódulo controla los tipos de inventario y define cómo se estructura la captura de productos para cada tipo.',
        pageTitle: 'Gestion de tipos',
        pageDescription: 'Puedes buscar tipos, registrar uno nuevo y acceder al detalle para editar configuraciónes de campos.',
        steps: [
            {
                selector: '[data-tour-inventory-types-search]',
                title: 'Búsqueda del catálogo',
                description: 'Usa este buscador para filtrar tipos de inventario por nombre.'
            },
            {
                selector: '[data-tour-inventory-types-register]',
                title: 'Nuevo tipo',
                description: 'Este botón abre el formulario para registrar un nuevo tipo de inventario.'
            },
            {
                selector: '[data-tour-inventory-types-table]',
                title: 'Tabla de tipos',
                description: 'En esta tabla revisas los tipos existentes y abres acciones de consulta o edición.'
            }
        ]
    },
    {
        key: 'inventory-type-register',
        sidebar: 'Inventario',
        match: pathname => pathname === '/settings/inventory-types/register',
        introTitle: 'Formulario de tipo de inventario',
        introDescription: 'Este submódulo define el nombre del tipo y los campos configurables que verás al registrar productos de inventario.',
        pageTitle: 'Definición del tipo',
        pageDescription: 'Aquí eliges la estructura de captura para ese tipo y cierras el flujo guardando cambios.',
        steps: [
            {
                selector: '[data-tour-inventory-type-form]',
                title: 'Configuración del tipo',
                description: 'En esta seccion capturas el nombre del tipo y seleccionas los campos habilitados para su formulario de productos.'
            },
            {
                selector: '[data-tour-inventory-type-form]',
                title: 'Impacto en el registro de productos',
                description: 'Este tipo controla que campos se muestran en Registrar producto de inventario. Al guardar aquí, cambias la captura operativa de ese formulario.'
            },
            {
                selector: '[data-tour-inventory-type-actions]',
                title: 'Acciones del formulario',
                description: 'Estas acciones permiten guardar la configuración o cancelar y volver al listado de tipos.'
            }
        ]
    },
    {
        key: 'commitment-types-listado',
        sidebar: 'Agenda',
        match: pathname => pathname === '/settings/commitment-types',
        introTitle: 'Catalogo de tipos de compromiso',
        introDescription: 'Este submódulo administra los tipos usados en agenda para clasificar compromisos y citas con mayor orden operativo.',
        pageTitle: 'Gestion de compromisos',
        pageDescription: 'Desde aquí puedes buscar tipos, registrar nuevos y entrar al detalle para editar configuraciónes existentes.',
        steps: [
            {
                selector: '[data-tour-commitment-types-search]',
                title: 'Búsqueda del catálogo',
                description: 'Filtra los tipos de compromiso por nombre para encontrarlos más rápido.'
            },
            {
                selector: '[data-tour-commitment-types-register]',
                title: 'Registrar tipo',
                description: 'Este acceso abre el formulario para crear un nuevo tipo de compromiso.'
            },
            {
                selector: '[data-tour-commitment-types-table]',
                title: 'Tabla de tipos',
                description: 'Aquí puedes revisar el catálogo y acceder a detalle o edición de cada registro.'
            }
        ]
    },
    {
        key: 'commitment-type-register',
        sidebar: 'Agenda',
        match: pathname => pathname === '/settings/commitment-types/register',
        introTitle: 'Formulario de tipo de compromiso',
        introDescription: 'Este submódulo te permite crear o editar un tipo de compromiso con su nombre y descripción operativa.',
        pageTitle: 'Captura del tipo',
        pageDescription: 'Completa la información y finaliza guardando cambios o regresando al listado.',
        steps: [
            {
                selector: '[data-tour-commitment-type-form]',
                title: 'Datos del tipo',
                description: 'En esta seccion defines nombre y descripción del tipo de compromiso.'
            },
            {
                selector: '[data-tour-commitment-type-form]',
                title: 'Uso dentro de agenda',
                description: 'Los tipos creados aquí aparecen en el formulario Registrar agenda para clasificar eventos y compromisos.'
            },
            {
                selector: '[data-tour-commitment-type-actions]',
                title: 'Guardar o cancelar',
                description: 'Estas acciones cierran el flujo de alta o edición del tipo de compromiso.'
            }
        ]
    },
    {
        key: 'settings-profiles',
        sidebar: 'Configuración',
        match: pathname => pathname === '/settings/profiles',
        introTitle: 'Gestion de perfiles',
        introDescription: 'Este submódulo permite administrar cuentas del sistema, consultar su rol y revisar acciones históricas por usuario.',
        pageTitle: 'Listado de usuarios',
        pageDescription: 'Puedes filtrar perfiles y abrir detalle para revisar información completa o ajustar permisos.',
        steps: [
            {
                selector: '[data-tour-settings-profiles-search]',
                title: 'Búsqueda de usuarios',
                description: 'Usa este filtro para localizar usuarios por nombre, correo o rol.'
            },
            {
                selector: '[data-tour-settings-profiles-table]',
                title: 'Tabla de perfiles',
                description: 'Aquí revisas usuarios activos y ejecutas acciones de detalle e historial.'
            }
        ]
    },
    {
        key: 'settings-profile-detail',
        sidebar: 'Configuración',
        match: pathname => pathname.startsWith('/settings/profiles/'),
        introTitle: 'Detalle de perfil',
        introDescription: 'Este submódulo muestra información completa de una cuenta y permite actualizar su rol con validación administrativa.',
        pageTitle: 'Administracion del perfil',
        pageDescription: 'Aquí validas datos de la cuenta, seleccionas un nuevo rol y confirmas el cambio con contraseña de administrador.',
        steps: [
            {
                selector: '[data-tour-settings-profile-detail-info]',
                title: 'Resumen del usuario',
                description: 'Esta seccion concentra datos personales, estado de cuenta y metadatos del usuario.'
            },
            {
                selector: '[data-tour-settings-profile-detail-actions]',
                title: 'Confirmar cambios de rol',
                description: 'Desde aquí puedes guardar el cambio de rol o volver al listado de perfiles.'
            }
        ]
    },
    {
        key: 'settings-profile',
        sidebar: 'Configuración',
        match: pathname => pathname === '/settings/profile',
        introTitle: 'Configuración de perfil médico',
        introDescription: 'Este submódulo permite al doctor mantener su información profesional y su foto de perfil actualizadas.',
        pageTitle: 'Edicion de perfil',
        pageDescription: 'Aquí puedes modificar datos de contacto, especialidad, biografía y guardar los cambios de tu perfil.',
        steps: [
            {
                selector: '[data-tour-settings-profile-fields]',
                title: 'Datos profesionales',
                description: 'En esta zona capturas información de perfil, incluyendo foto, especialidad y datos de contacto.'
            },
            {
                selector: '[data-tour-settings-profile-actions]',
                title: 'Guardar configuración',
                description: 'Estas acciones te permiten guardar cambios o reiniciar el formulario del perfil médico.'
            }
        ]
    },
    {
        key: 'settings-display',
        sidebar: 'Configuración',
        match: pathname => pathname === '/settings/display',
        introTitle: 'Configuración de pantalla',
        introDescription: 'Este submódulo agrupa ajustes de apariencia y accesibilidad para la interfaz del usuario actual.',
        pageTitle: 'Preferencias visuales',
        pageDescription: 'Aquí puedes modificar tema, contraste, dirección del layout, escala tipográfica y fullscreen.',
        steps: [
            {
                selector: '[data-tour-settings-display-controls]',
                title: 'Controles visuales',
                description: 'Cada tarjeta ajusta una preferencia de interfaz para adaptar la aplicacion a la forma de trabajo del usuario.'
            },
            {
                selector: '[data-tour-settings-display-reset]',
                title: 'Restablecer configuración',
                description: 'Esta acción devuelve la configuración visual a su estado base si necesitas empezar de nuevo.'
            }
        ]
    }
];

export const getModuleTour = pathname => moduleTours.find(module => module.match(pathname)) || null;

export const getModuleTourKeys = () => moduleTours.map(module => module.key);

if (process.env.NODE_ENV === 'development') {
    const drift = getTutorialCatalogDrift(getModuleTourKeys());

    if (!drift.inSync) {
        console.warn('[tutorialModules] Catálogo desincronizado con moduleTours.js', drift);
    }
}
